import DOMPurify from "dompurify";
import { marked, Renderer } from "marked";

const ZARVIS_MARKDOWN_TAGS = [
  "blockquote",
  "br",
  "code",
  "del",
  "em",
  "h1",
  "h2",
  "h3",
  "h4",
  "hr",
  "li",
  "ol",
  "p",
  "pre",
  "strong",
  "table",
  "tbody",
  "td",
  "th",
  "thead",
  "tr",
  "ul",
] as const;

const QUANTIFIED_RESULT_PATTERN = /(?:(?:[+-]\s*)?[$\u20ac\u00a3\u20b9]\s*[+-]?|[+-]?)(?:\d{1,3}(?:,\d{3})+|\d+)(?:\.\d+)?(?:\s*(?:-|to)\s*(?:[$\u20ac\u00a3\u20b9]\s*)?(?:\d{1,3}(?:,\d{3})+|\d+)(?:\.\d+)?)?(?:\s*(?:%|percentage\s+points?|basis\s+points?|bps?|thousand|million|billion|crores?|lakhs?|mm|cm|kg|tonnes?|tons?|[kmb]|x))?/gi;
const EXPLICIT_RESULT_UNIT = /[$\u20ac\u00a3\u20b9%]|\b(?:percentage\s+points?|basis\s+points?|bps?|thousand|million|billion|crores?|lakhs?|mm|cm|kg|tonnes?|tons?|[kmb]|x)\b/i;
const POSITIVE_REVERSAL_CONTEXT = /\b(?:costs?|expenses?|spend|loss(?:es)?|deficit)\s+(?:fell|falls?|declin(?:e|ed|ing)|decreas(?:e|ed|ing)|drop(?:ped|ping)?|reduc(?:e|ed|tion)|down)\b/i;
const NEGATIVE_RESULT_CONTEXT = /\b(?:(?:profit|margin|revenue|savings?|gains?|growth)\s+(?:fell|falls?|declin(?:e|ed|ing)|decreas(?:e|ed|ing)|drop(?:ped|ping)?|down)|loss(?:es)?|deficit|shortfall|overspend(?:ing)?|negative|cost\s+overrun|margin\s+erosion|below\s+(?:plan|target|baseline)|(?:costs?|expenses?|spend)\s+(?:rose|rises?|increas(?:e|ed|ing)|growth|up))\b/i;
const POSITIVE_RESULT_CONTEXT = /\b(?:profit(?:ability)?|savings?|gains?|upside|surplus|growth|improv(?:e|ed|ement)|outperform(?:ed|ance)?|above\s+(?:plan|target|baseline)|margin\s+expansion|cost\s+reduction)\b/i;
const CAUTION_RESULT_CONTEXT = /\b(?:assum(?:e|ed|ption)|approximat(?:e|ely)|estimate(?:d)?|indicative|potential|projected|tentative|uncertain(?:ty)?|unverified|not\s+validated|cannot\s+be\s+validated|subject\s+to)\b/i;

type ZarvisResultTone = "positive" | "negative" | "caution" | "neutral";

interface ZarvisResultContext {
  tone: "positive" | "negative" | null;
  caution: boolean;
}

interface ResolvedZarvisResultStyle {
  tone: ZarvisResultTone;
  caution: boolean;
}

function classifyResultContext(value: string): ZarvisResultContext {
  const positiveReversal = POSITIVE_REVERSAL_CONTEXT.test(value);
  const negative = !positiveReversal && NEGATIVE_RESULT_CONTEXT.test(value);
  const positive = positiveReversal || (!negative && POSITIVE_RESULT_CONTEXT.test(value));
  return {
    tone: negative ? "negative" : positive ? "positive" : null,
    caution: CAUTION_RESULT_CONTEXT.test(value),
  };
}

function sentenceAround(value: string, start: number, end: number): string {
  let sentenceStart = start;
  let sentenceEnd = end;
  while (sentenceStart > 0 && !/[.!?;\n]/.test(value[sentenceStart - 1])) {
    sentenceStart -= 1;
  }
  while (sentenceEnd < value.length && !/[.!?;\n]/.test(value[sentenceEnd])) {
    sentenceEnd += 1;
  }
  return value.slice(sentenceStart, sentenceEnd);
}

function explicitSignedTone(value: string): "positive" | "negative" | null {
  const normalized = value.trim().replace(/^[$\u20ac\u00a3\u20b9]\s*/, "");
  if (normalized.startsWith("+")) return "positive";
  if (normalized.startsWith("-")) return "negative";
  return null;
}

function isDateFragment(value: string, start: number, end: number): boolean {
  const token = value.slice(start, end).trim();
  return value[start - 1] === "-"
    || value[end] === "-"
    || (/^(?:19|20)\d{2}$/.test(token) && !EXPLICIT_RESULT_UNIT.test(token));
}

function textNodesWithin(element: HTMLElement): Text[] {
  const textNodes: Text[] = [];
  const visit = (node: Node): void => {
    for (const child of Array.from(node.childNodes)) {
      if (child.nodeType === 3) {
        textNodes.push(child as Text);
      } else {
        visit(child);
      }
    }
  };
  visit(element);
  return textNodes;
}

function resultStyle(
  token: string,
  source: string,
  start: number,
  end: number,
  fallbackContext: string,
): ResolvedZarvisResultStyle | null {
  if (isDateFragment(source, start, end)) return null;
  const localContext = classifyResultContext(sentenceAround(source, start, end));
  const broadContext = classifyResultContext(fallbackContext);
  const context = localContext.tone || localContext.caution
    ? localContext
    : broadContext;
  const signedTone = explicitSignedTone(token);
  const explicitMetric = EXPLICIT_RESULT_UNIT.test(token) || signedTone !== null;
  const contextTone = signedTone ?? context.tone;
  if (!explicitMetric && contextTone === null) return null;
  return {
    tone: contextTone ?? (context.caution ? "caution" : "neutral"),
    caution: context.caution,
  };
}

function decorateQuantifiedResults(html: string): string {
  if (typeof document === "undefined") return html;
  const template = document.createElement("template");
  template.innerHTML = html;

  for (const container of Array.from(
    template.content.querySelectorAll<HTMLElement>("p, li, td"),
  )) {
    const fallbackContext = container.tagName === "TD"
      ? container.closest("tr")?.textContent ?? container.textContent ?? ""
      : container.textContent ?? "";

    for (const textNode of textNodesWithin(container)) {
      const parent = textNode.parentElement;
      if (!parent || parent.closest("code, pre, [data-zarvis-tone]")) continue;
      const value = textNode.data;
      const matches = Array.from(value.matchAll(QUANTIFIED_RESULT_PATTERN));
      if (matches.length === 0) continue;

      const existingEmphasis = parent.closest<HTMLElement>("strong, em");
      if (existingEmphasis && container.contains(existingEmphasis)) {
        for (const match of matches) {
          const token = match[0];
          const start = match.index ?? 0;
          const style = resultStyle(
            token,
            value,
            start,
            start + token.length,
            fallbackContext,
          );
          if (!style) continue;
          existingEmphasis.dataset.zarvisTone = style.tone;
          if (style.caution && style.tone !== "caution") {
            existingEmphasis.dataset.zarvisCaution = "true";
          }
        }
        continue;
      }

      const fragment = textNode.ownerDocument.createDocumentFragment();
      let cursor = 0;
      let changed = false;
      for (const match of matches) {
        const token = match[0];
        const start = match.index ?? 0;
        const end = start + token.length;
        const style = resultStyle(token, value, start, end, fallbackContext);

        fragment.append(value.slice(cursor, start));
        if (!style) {
          fragment.append(token);
          cursor = end;
          continue;
        }

        const semanticElement = textNode.ownerDocument.createElement(
          style.tone === "caution" ? "em" : "strong",
        );
        semanticElement.dataset.zarvisTone = style.tone;
        if (style.caution && style.tone !== "caution") {
          semanticElement.dataset.zarvisCaution = "true";
        }
        semanticElement.textContent = token;
        fragment.append(semanticElement);
        cursor = end;
        changed = true;
      }
      if (!changed) continue;
      fragment.append(value.slice(cursor));
      textNode.replaceWith(fragment);
    }
  }

  return DOMPurify.sanitize(template.innerHTML, {
    ALLOWED_TAGS: [...ZARVIS_MARKDOWN_TAGS],
    ALLOWED_ATTR: ["data-zarvis-tone", "data-zarvis-caution"],
  });
}

/**
 * Render untrusted model markdown without active or remotely loading content.
 *
 * Links are intentionally reduced to plain text. Zarvis evidence navigation is
 * provided through the separately validated citation components.
 */
export function renderZarvisMarkdown(source: string): string {
  const renderer = new Renderer();
  renderer.html = () => "";
  renderer.image = ({ text }) => escapeHtml(text);
  renderer.link = ({ tokens }) => renderer.parser.parseInline(tokens);
  const html = marked.parse(source, { async: false, renderer });
  const sanitized = DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [...ZARVIS_MARKDOWN_TAGS],
    ALLOWED_ATTR: [],
  });
  return decorateQuantifiedResults(sanitized);
}

function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, (character) => ({
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#39;",
  })[character] ?? character);
}
